import { useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { useContactFields } from "../components/ContactFields";
import type { CachedContact } from "../lib/types";

interface Props {
  // Pre-fills the contact form if we end up rendering it.
  initialContact: CachedContact | null;
  // True when no session row has been created for this event yet (Welcome
  // was skipped). Trigger comes from a localStorage flag rather than the
  // cached-contact heuristic — stale empty cache values used to fool it.
  showContact: boolean;
  submitting: boolean;
  onSubmit: (payload: {
    brandWords: string;
    contact: {
      contactName: string | null;
      email: string | null;
      phone: string | null;
      zip: string | null;
      marketingOptIn: boolean;
    } | null;
    display: CachedContact | null;
  }) => void;
}

function countWords(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

export function WrapUpScreen({ initialContact, showContact, submitting, onSubmit }: Props) {
  const [brand, setBrand] = useState("");
  const contact = useContactFields(initialContact, { showZip: false });

  const words = countWords(brand);
  const overLimit = words > 3;
  const brandOk = words >= 1 && words <= 3;

  // Contact fields are only blocking if we're showing them.
  const canSubmit = brandOk && (!showContact || contact.isValid) && !submitting;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      brandWords: brand.trim(),
      contact: showContact ? contact.payload : null,
      display: showContact ? contact.display : null,
    });
  };

  return (
    <main className="shell flex flex-col px-6 pb-8 pt-10">
      <header className="mb-6 flex justify-center">
        <BrandMark className="h-14 w-auto" />
      </header>

      <div className="flex flex-1 flex-col">
        <div
          className="mb-6 flex h-16 w-16 items-center justify-center self-start rounded-full bg-amber/15"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-8 w-8 text-amber"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label="Checkmark"
          >
            <title>Checkmark</title>
            <path d="M5 12.5 10 17.5 19 7" />
          </svg>
        </div>

        <p className="mb-2 text-xs uppercase tracking-[0.3em] text-amber">All 4 done</p>
        <h1 className="text-4xl text-cream">One last thing.</h1>
        <p className="mt-3 text-cream-dim">
          {showContact
            ? "Describe 1976 Barbecue in 1–3 words. Join the club below to enter for the meal for two."
            : "You're entered to win a free meal for two. Describe 1976 Barbecue in 1–3 words and you're done."}
        </p>

        <div className="mt-6">
          <label
            htmlFor="brand-words"
            className="mb-2 block text-sm uppercase tracking-wider text-cream-dim"
          >
            The brand today
          </label>
          <input
            id="brand-words"
            type="text"
            // biome-ignore lint/a11y/noAutofocus: this input is the primary action on the wrap-up screen
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={true}
            value={brand}
            onChange={(e) => setBrand(e.target.value.slice(0, 60))}
            placeholder="bold, smoky, real…"
            className="field"
          />
          <div className="mt-1 flex justify-between text-xs">
            <span className={overLimit ? "text-ember" : "text-cream-dim/60"}>
              {overLimit ? "Try keeping it to 3 words." : "1 to 3 words is plenty."}
            </span>
            <span className="text-cream-dim/60">
              {words} {words === 1 ? "word" : "words"}
            </span>
          </div>
        </div>

        {showContact && <div className="mt-8">{contact.fields}</div>}
      </div>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={submit}
        className="pill-btn pill-btn-primary mt-6"
      >
        {submitting ? "Sending…" : "Finish"}
      </button>
    </main>
  );
}
